import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchCasaMapaPublicInventory,
  setCasaMapaPublicListing,
  updateCasaMapaPublicCriteria,
} from "../../../api/clientCasamapaAdminApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useCurrency, useLocale } from "../../../intl";
import "../../../styles/casamapaAdminPortal.css";
import CasaMapaPublicInventoryDetailModal from "./CasaMapaPublicInventoryDetailModal";

const COPY = "realEstate.casaMapa.admin.publicInventory";

const SORTABLE_COLUMNS = [
  { key: "title", labelKey: "property" },
  { key: "place", labelKey: "place" },
  { key: "agency", labelKey: "agency" },
  { key: "price", labelKey: "price" },
  { key: "status", labelKey: "status" },
];

function reasonLabel(reason, t) {
  return t(`${COPY}.reasons.${reason}`, {}, reason);
}

function CollapsiblePanel({ title, hint, headerAction, children, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const titleId = `${String(title || "panel").replace(/\s+/g, "-").toLowerCase()}-title`;

  return (
    <section className="panel-card casamapa-admin-section cm-admin-collapse">
      <button
        type="button"
        className="casamapa-admin-section__header cm-admin-collapse__summary"
        aria-expanded={isOpen}
        aria-controls={`${titleId}-body`}
        onClick={() => setIsOpen((current) => !current)}
      >
        <div>
          <h2 id={titleId}>{title}</h2>
          {hint ? <p className="muted">{hint}</p> : null}
        </div>
        <span className="cm-admin-collapse__chevron" aria-hidden="true" />
      </button>
      {isOpen ? (
        <div className="cm-admin-collapse__body" id={`${titleId}-body`}>
          {headerAction ? <div className="cm-admin-collapse__toolbar">{headerAction}</div> : null}
          {children}
        </div>
      ) : null}
    </section>
  );
}

function VisibilityBadge({ visibility, t }) {
  if (visibility?.via === "manual") {
    return <span className="cm-pub-inv__badge cm-pub-inv__badge--manual">{t(`${COPY}.viaManual`)}</span>;
  }
  if (visibility?.isPublic) {
    return <span className="cm-pub-inv__badge cm-pub-inv__badge--public">{t(`${COPY}.viaCriteria`)}</span>;
  }
  return <span className="cm-pub-inv__badge cm-pub-inv__badge--hidden">{t(`${COPY}.hidden`)}</span>;
}

function FlagBadges({ flags, t }) {
  if (!flags?.length) return null;
  return (
    <div className="cm-pub-inv__flag-row">
      {flags.map((flag) => (
        <span
          key={flag.flagId || flag.flagType}
          className={`cm-pub-inv__flag${flag.flagType === "duplicate" ? " cm-pub-inv__flag--duplicate" : ""}`}
        >
          {t(`${COPY}.flagTypes.${flag.flagType}`, {}, flag.flagType)}
        </span>
      ))}
    </div>
  );
}

function SortableHeader({ column, sortBy, sortDir, onSort, label }) {
  const active = sortBy === column;
  const ariaSort = active ? (sortDir === "desc" ? "descending" : "ascending") : "none";
  return (
    <th aria-sort={ariaSort}>
      <button
        type="button"
        className={`cm-pub-inv__sort${active ? " is-active" : ""}`}
        onClick={() => onSort(column)}
      >
        <span>{label}</span>
        <span className="cm-pub-inv__sort-icon" aria-hidden="true">
          {active ? (sortDir === "desc" ? "↓" : "↑") : "↕"}
        </span>
      </button>
    </th>
  );
}

export default function CasaMapaAdminPublicInventoryPage() {
  const { clientViewId, path } = useCasaMapaAdminPortal();
  const { t } = useLocale();
  const { formatMoney } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [q, setQ] = useState("");
  const [visibility, setVisibility] = useState("all");
  const [listingType, setListingType] = useState("");
  const [municipality, setMunicipality] = useState("");
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState("title");
  const [sortDir, setSortDir] = useState("asc");
  const [draftSources, setDraftSources] = useState(new Set());
  const [draftCities, setDraftCities] = useState([]);
  const [draftListingType, setDraftListingType] = useState("both");
  const [extraCity, setExtraCity] = useState("");
  const [busyId, setBusyId] = useState("");
  const [selectedListingId, setSelectedListingId] = useState("");

  const applyCriteriaDraft = (result) => {
    setDraftSources(new Set(result.criteria?.sourceKeys || []));
    setDraftCities(result.criteria?.municipalities || []);
    setDraftListingType(result.criteria?.listingType || "both");
  };

  const load = useCallback(
    async (nextOffset = 0, { syncCriteria = false, nextSortBy = sortBy, nextSortDir = sortDir } = {}) => {
      if (!clientViewId) return;
      setLoading(true);
      setError("");
      try {
        const result = await fetchCasaMapaPublicInventory(clientViewId, {
          q: q || undefined,
          visibility: visibility === "all" ? undefined : visibility,
          listingType: listingType || undefined,
          municipality: municipality || undefined,
          sortBy: nextSortBy,
          sortDir: nextSortDir,
          limit: 40,
          offset: nextOffset,
        });
        setData(result);
        setOffset(nextOffset);
        if (syncCriteria) {
          applyCriteriaDraft(result);
        }
      } catch (err) {
        setError(err.message || t(`${COPY}.loadError`));
      } finally {
        setLoading(false);
      }
    },
    [clientViewId, listingType, municipality, q, sortBy, sortDir, t, visibility]
  );

  useEffect(() => {
    load(0, { syncCriteria: true });
  }, [clientViewId]);

  const agencies = data?.options?.agencies || [];
  const cityOptions = useMemo(() => {
    const recommended = data?.options?.recommendedMunicipalities || [];
    return [...new Set([...recommended, ...draftCities])].sort((left, right) =>
      left.localeCompare(right, "es")
    );
  }, [data, draftCities]);

  const toggleAgency = (agency) => {
    setDraftSources((current) => {
      const next = new Set(current);
      const enabled = agency.sourceKeys.some((key) => next.has(key));
      for (const key of agency.sourceKeys) {
        if (enabled) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  };

  const toggleCity = (city) => {
    setDraftCities((current) =>
      current.includes(city) ? current.filter((value) => value !== city) : [...current, city]
    );
  };

  const addExtraCity = () => {
    const city = extraCity.trim();
    if (!city) return;
    setDraftCities((current) => (current.includes(city) ? current : [...current, city]));
    setExtraCity("");
  };

  const onSort = (column) => {
    const nextDir = sortBy === column && sortDir === "asc" ? "desc" : "asc";
    setSortBy(column);
    setSortDir(nextDir);
    load(0, { nextSortBy: column, nextSortDir: nextDir });
  };

  const saveCriteria = async () => {
    if (!clientViewId) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const result = await updateCasaMapaPublicCriteria(clientViewId, {
        sourceKeys: [...draftSources],
        municipalities: draftCities,
        listingType: draftListingType,
        query: {
          q: q || undefined,
          visibility: visibility === "all" ? undefined : visibility,
          listingType: listingType || undefined,
          municipality: municipality || undefined,
          sortBy,
          sortDir,
          limit: 40,
          offset,
        },
      });
      setData(result);
      applyCriteriaDraft(result);
      setNotice(t(`${COPY}.saved`));
    } catch (err) {
      setError(err.message || t(`${COPY}.saveError`));
    } finally {
      setSaving(false);
    }
  };

  const setListingPublic = async (listing, included) => {
    if (!clientViewId) return;
    setBusyId(listing.id);
    setError("");
    setNotice("");
    try {
      const result = await setCasaMapaPublicListing(clientViewId, listing.id, included);
      const wasPublic = Boolean(listing.publicVisibility?.isPublic);
      const nowPublic = Boolean(result.listing?.publicVisibility?.isPublic);
      const wasManual = listing.publicVisibility?.via === "manual";
      const nowManual = result.listing?.publicVisibility?.via === "manual";
      setData((current) => {
        if (!current) return current;
        const publicDelta = Number(nowPublic) - Number(wasPublic);
        const manualDelta = Number(nowManual) - Number(wasManual);
        return {
          ...current,
          counts: {
            ...current.counts,
            public: Math.max(0, (current.counts?.public || 0) + publicDelta),
            hidden: Math.max(0, (current.counts?.hidden || 0) - publicDelta),
            manual: Math.max(0, (current.counts?.manual || 0) + manualDelta),
          },
          listings: current.listings.map((row) => (row.id === listing.id ? result.listing : row)),
        };
      });
      setNotice(included ? t(`${COPY}.added`) : t(`${COPY}.removed`));
    } catch (err) {
      setError(err.message || t(`${COPY}.listingError`));
    } finally {
      setBusyId("");
    }
  };

  const handleFlagsChanged = (listingId, openFlags) => {
    setData((current) => {
      if (!current) return current;
      return {
        ...current,
        listings: current.listings.map((row) =>
          row.id === listingId ? { ...row, flags: openFlags } : row
        ),
      };
    });
  };

  if (!clientViewId) {
    return <p className="muted">Select a CasaMapa client view to continue.</p>;
  }

  return (
    <section className="casamapa-admin-page cm-pub-inv">
      <header className="casamapa-admin-page__header">
        <div>
          <div className="eyebrow">{t("realEstate.casaMapa.admin.portalEyebrow")}</div>
          <h1>{t(`${COPY}.title`)}</h1>
          <p className="muted">{t(`${COPY}.subtitle`)}</p>
        </div>
      </header>

      {error ? <p className="admin-data-health-error">{error}</p> : null}
      {notice ? <p className="cm-pub-inv__notice">{notice}</p> : null}

      <div className="casamapa-admin-kpi-grid">
        <article className="casamapa-admin-kpi">
          <span className="casamapa-admin-kpi__label">{t(`${COPY}.synced`)}</span>
          <strong className="casamapa-admin-kpi__value">
            {(data?.counts?.synced || 0).toLocaleString()}
          </strong>
        </article>
        <article className="casamapa-admin-kpi">
          <span className="casamapa-admin-kpi__label">{t(`${COPY}.publicCount`)}</span>
          <strong className="casamapa-admin-kpi__value">
            {(data?.counts?.public || 0).toLocaleString()}
          </strong>
        </article>
        <article className="casamapa-admin-kpi">
          <span className="casamapa-admin-kpi__label">{t(`${COPY}.hiddenCount`)}</span>
          <strong className="casamapa-admin-kpi__value">
            {(data?.counts?.hidden || 0).toLocaleString()}
          </strong>
        </article>
        <article className="casamapa-admin-kpi">
          <span className="casamapa-admin-kpi__label">{t(`${COPY}.manualCount`)}</span>
          <strong className="casamapa-admin-kpi__value">
            {(data?.counts?.manual || 0).toLocaleString()}
          </strong>
        </article>
      </div>

      <CollapsiblePanel
        title={t(`${COPY}.criteriaTitle`)}
        hint={t(`${COPY}.criteriaHint`)}
        headerAction={
          <button
            type="button"
            className="cm-listing-health__fix-button"
            disabled={saving}
            onClick={saveCriteria}
          >
            {saving ? t(`${COPY}.saving`) : t(`${COPY}.saveCriteria`)}
          </button>
        }
      >
        <fieldset className="cm-pub-inv__fieldset">
          <legend>{t(`${COPY}.listingType`)}</legend>
          {["both", "sale", "rent"].map((value) => (
            <label key={value}>
              <input
                type="radio"
                name="public-listing-type"
                checked={draftListingType === value}
                onChange={() => setDraftListingType(value)}
              />
              {t(`${COPY}.types.${value}`)}
            </label>
          ))}
        </fieldset>

        <fieldset className="cm-pub-inv__fieldset">
          <legend>{t(`${COPY}.municipalities`)}</legend>
          <div className="cm-pub-inv__chips">
            {cityOptions.map((city) => (
              <label key={city} className="cm-pub-inv__chip">
                <input type="checkbox" checked={draftCities.includes(city)} onChange={() => toggleCity(city)} />
                {city}
              </label>
            ))}
          </div>
          <div className="cm-pub-inv__add-city">
            <input
              value={extraCity}
              onChange={(event) => setExtraCity(event.target.value)}
              placeholder={t(`${COPY}.addCityPlaceholder`)}
            />
            <button type="button" className="casamapa-admin-link-button" onClick={addExtraCity}>
              {t(`${COPY}.addCity`)}
            </button>
          </div>
        </fieldset>

        <fieldset className="cm-pub-inv__fieldset">
          <legend>{t(`${COPY}.agencies`)}</legend>
          <button
            type="button"
            className="casamapa-admin-link-button"
            onClick={() =>
              setDraftSources(new Set(agencies.flatMap((agency) => agency.sourceKeys || [])))
            }
          >
            {t(`${COPY}.enableAllAgencies`)}
          </button>
          <div className="cm-pub-inv__agency-grid">
            {agencies.map((agency) => {
              const enabled = agency.sourceKeys.some((key) => draftSources.has(key));
              return (
                <label key={agency.slug} className="cm-pub-inv__agency">
                  <input type="checkbox" checked={enabled} onChange={() => toggleAgency(agency)} />
                  <span>
                    <strong>{agency.name}</strong>
                    <small>{agency.publicationStatus}</small>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      </CollapsiblePanel>

      <CollapsiblePanel title={t(`${COPY}.listingsTitle`)} hint={t(`${COPY}.listingsHint`)}>
        <form
          className="cm-pub-inv__filters"
          onSubmit={(event) => {
            event.preventDefault();
            load(0);
          }}
        >
          <input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder={t(`${COPY}.searchPlaceholder`)}
          />
          <select value={visibility} onChange={(event) => setVisibility(event.target.value)}>
            <option value="all">{t(`${COPY}.filterAll`)}</option>
            <option value="public">{t(`${COPY}.filterPublic`)}</option>
            <option value="hidden">{t(`${COPY}.filterHidden`)}</option>
            <option value="manual">{t(`${COPY}.filterManual`)}</option>
          </select>
          <select value={listingType} onChange={(event) => setListingType(event.target.value)}>
            <option value="">{t(`${COPY}.types.both`)}</option>
            <option value="sale">{t(`${COPY}.types.sale`)}</option>
            <option value="rent">{t(`${COPY}.types.rent`)}</option>
          </select>
          <select value={municipality} onChange={(event) => setMunicipality(event.target.value)}>
            <option value="">{t(`${COPY}.allCities`)}</option>
            {cityOptions.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
          <button type="submit" className="casamapa-admin-link-button">
            {t(`${COPY}.search`)}
          </button>
        </form>

        {loading ? <p className="muted">{t(`${COPY}.loading`)}</p> : null}

        <div className="cm-pub-inv__table-wrap">
          <table className="cm-pub-inv__table">
            <thead>
              <tr>
                {SORTABLE_COLUMNS.map((column) => (
                  <SortableHeader
                    key={column.key}
                    column={column.key}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={onSort}
                    label={t(`${COPY}.${column.labelKey}`)}
                  />
                ))}
                <th>{t(`${COPY}.action`)}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.listings || []).map((listing) => (
                <tr key={listing.id}>
                  <td>
                    <button
                      type="button"
                      className="cm-pub-inv__title-button"
                      onClick={() => setSelectedListingId(listing.id)}
                    >
                      <strong>{listing.title || t(`${COPY}.untitled`)}</strong>
                    </button>
                    <small>
                      {[listing.listingType, listing.propertyType].filter(Boolean).join(" · ")}
                    </small>
                    <FlagBadges flags={listing.flags} t={t} />
                  </td>
                  <td>{[listing.neighborhood, listing.municipality].filter(Boolean).join(", ") || "—"}</td>
                  <td>{listing.agencyName || listing.sourceKey || "—"}</td>
                  <td>
                    {listing.priceCop != null
                      ? formatMoney(listing.priceCop, { storageCurrency: "COP" })
                      : "—"}
                  </td>
                  <td>
                    <VisibilityBadge visibility={listing.publicVisibility} t={t} />
                    {!listing.publicVisibility?.isPublic && listing.publicVisibility?.reasons?.length ? (
                      <small>
                        {listing.publicVisibility.reasons.map((reason) => reasonLabel(reason, t)).join(", ")}
                      </small>
                    ) : null}
                  </td>
                  <td>
                    {listing.publicVisibility?.via === "manual" ? (
                      <button
                        type="button"
                        className="casamapa-admin-link-button"
                        disabled={busyId === listing.id}
                        onClick={() => setListingPublic(listing, false)}
                      >
                        {t(`${COPY}.removePublic`)}
                      </button>
                    ) : listing.publicVisibility?.isPublic ? (
                      <span className="muted">{t(`${COPY}.alreadyPublic`)}</span>
                    ) : (
                      <button
                        type="button"
                        className="cm-listing-health__fix-button"
                        disabled={busyId === listing.id}
                        onClick={() => setListingPublic(listing, true)}
                      >
                        {t(`${COPY}.addPublic`)}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && !data?.listings?.length ? <p className="muted">{t(`${COPY}.empty`)}</p> : null}

        {data?.pagination?.hasMore || offset > 0 ? (
          <div className="cm-pub-inv__pager">
            <button
              type="button"
              className="casamapa-admin-link-button"
              disabled={offset === 0 || loading}
              onClick={() => load(Math.max(0, offset - 40))}
            >
              {t(`${COPY}.previous`)}
            </button>
            <button
              type="button"
              className="casamapa-admin-link-button"
              disabled={!data?.pagination?.hasMore || loading}
              onClick={() => load(offset + 40)}
            >
              {t(`${COPY}.next`)}
            </button>
          </div>
        ) : null}
      </CollapsiblePanel>

      {selectedListingId ? (
        <CasaMapaPublicInventoryDetailModal
          clientViewId={clientViewId}
          listingId={selectedListingId}
          path={path}
          onClose={() => setSelectedListingId("")}
          onFlagsChanged={handleFlagsChanged}
        />
      ) : null}
    </section>
  );
}
